import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { useOfficeTutorial } from '~/components/OfficeTutorialPopup';
import { useGetDashboardJobInfo } from '~/domain/DashboardJobs/useCases/useGetDashboardJobInfo';
import { shouldUseDsoStructure } from '~/growthbook';
import { useAppSelector } from '~/hooks';
import { IAllowedRequest } from '~/models';
import { useFeatureFlagVariant } from '~/hooks/useFeatureFlagVariant';
import { useUserSelector } from '~/hooks/useUserSelector';
import { PendingAchVerificationBanner } from '~/components/Payments/PendingAchVerificationBanner';
import { routes } from '~/routes';
import { useIsWorkerClassificationEnabled } from '~/selectors';
import { feedbackRequire, fetchJobsWithAdjustmts, hideRegistrationPopup } from '../../actions';
import { Column, Ribbon } from '@tempmee/react';
import { OutlineWarning01Icon, SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';
import { ConfirmPopup } from '../../commonComponents';
import { SelectWorkerTypePopup } from '../../commonComponents/popups/SelectWorkerTypePopup/SelectWorkerTypePopup';
import history from '../../browserHistory';
import { Colors } from '../../themes/colors';
import '../../themes/inbox.scss';
import { RemoteConfigUtils } from '../../utils/RemoteConfigUtils';
import Inbox from '../Inbox';
import { PrimaryOfficeContactPopup } from '../PrimaryOfficeContactPopup';
import { ActiveShift } from './ActiveShift';
import { DomainDecommissioningWarningBanner } from './DomainDecommissioning';
import { useReferralPayout } from './hooks/useReferralPayout';
import { ShiftsSection } from './ShiftsSection';
import ShiftsWidget from './ShiftsWidget';
import { TopButtons } from './TopButtons';

const Dashboard = () => {
  const { showRegistrationSuccessPopup } = useAppSelector((state) => state.user);
  const { openTutorial } = useOfficeTutorial();
  const dashboardInfo = useAppSelector((state) => state.job.dashboardInfo);
  const dispatch = useDispatch();
  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);
  const user = useAppSelector((state) => state.user.user);
  // Check if the feature flag is set to 'test' variant for the new shifts widget
  const useNewShiftsWidget = useFeatureFlagVariant('guided-user-journey-dashboard', 'test');
  const { isLoading, refetch } = useGetDashboardJobInfo();

  useEffect(() => {
    refetch();
  }, [dispatch, selectedOfficeId]);

  useEffect(() => {
    if (showRegistrationSuccessPopup) {
      openTutorial('office-tutorial-dashboard-overview');
      dispatch(hideRegistrationPopup());
    }
  }, [dispatch, openTutorial, showRegistrationSuccessPopup]);

  return (
    <div data-cy="dashboard-container" style={{ width: '100%', maxWidth: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          justifyContent: 'space-between',
          alignContent: 'start',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <TopButtons showLocumTenens={user?.address?.[0]?.state === 'FL'} />
        <div
          style={{
            display: 'flex',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {useNewShiftsWidget ? (
            <ShiftsSection isLoading={isLoading} />
          ) : (
            <ShiftsWidget
              activeCount={dashboardInfo?.activeRequest.toString()}
              scheduledCount={dashboardInfo?.scheduled.toString()}
              actionRequiredCount={dashboardInfo?.actionRequired.toString()}
              isLoading={isLoading}
            />
          )}
        </div>

        <ActiveShift />

        <Inbox isRootComponent={false} />
      </div>
    </div>
  );
};

const dashboardRequestInterval = 1 * 60 * 60 * 1000; // 1 hour in milliseconds

interface IDashboardLocationState {
  showNotAllowedPopup?: boolean;
}

const DashboardPage = () => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const allowedRequest: Partial<IAllowedRequest> = user?.allowedRequest || {};
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { showNotAllowedPopup } = (locationState as IDashboardLocationState | null) || {};

  useReferralPayout();

  const canPostTempShifts = allowedRequest?.tempOrMdbShifts;
  const canPostPermanentHire = allowedRequest?.ppJob;
  const showPostingUnavailableWarning = !canPostTempShifts && !canPostPermanentHire;

  const statesToDisableTempRequests = RemoteConfigUtils.getDisabledTempPostStates();
  const showBlockedStateWarning =
    !showPostingUnavailableWarning &&
    (!allowedRequest?.tempOrMdbShifts ||
      statesToDisableTempRequests?.includes(user?.address?.[0]?.state ?? ''));

  const isWorkerClassificationEnabled = useIsWorkerClassificationEnabled();

  const showWorkerClassificationWarning =
    isWorkerClassificationEnabled && !user?.workerClassification?.classificationType;

  const selectedOfficeId = useAppSelector((state) => state.user.selectedOfficeId);

  useEffect(() => {
    if (!user) return;

    const prevDashboardRequestDate = localStorage.getItem(`prevDashboardRequestDate-${user.id}`);

    if (
      !prevDashboardRequestDate ||
      Date.now() - new Date(prevDashboardRequestDate).getTime() > dashboardRequestInterval
    ) {
      dispatch(feedbackRequire());
      dispatch(fetchJobsWithAdjustmts());

      localStorage.setItem(`prevDashboardRequestDate-${user.id}`, new Date().toISOString());
    }
  }, [dispatch, user?.id]);

  return (
    <div
      className="component_container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        gap: 20,
      }}
    >
      <Column style={{ gap: 8 }}>
        {showBlockedStateWarning && (
          <Ribbon variant="hard-warning" Icon={SolidExclamationWarningAlertIcon} data-cy="dashboard-blocked-state-warning">
            {'GoTu is not offering temporary services in your state as we work on building the best user experience for offices like you. However, "Permanent Hires" are still available!'}
          </Ribbon>
        )}

        {((shouldUseDsoStructure() && selectedOfficeId && showWorkerClassificationWarning) ||
          (!shouldUseDsoStructure() && showWorkerClassificationWarning)) && (
          <Ribbon
            variant="hard-warning"
            Icon={SolidExclamationWarningAlertIcon}
            data-cy="dashboard-worker-classification-warning"
            onClick={() => history.push(routes.workerClassificationType)}
          >
            Shift requests are unavailable until a worker type is selected for this office. Click here to set your preference.
          </Ribbon>
        )}

        <DomainDecommissioningWarningBanner />

        <PendingAchVerificationBanner dataCy="dashboard-pending-ach-warning" />

        {showPostingUnavailableWarning && (
          <Ribbon variant="hard-warning" Icon={SolidExclamationWarningAlertIcon} data-cy="dashboard-posting-unavailable-warning">
            Temporary and permanent job posting is currently disabled. Contact your Account Manager for support.
          </Ribbon>
        )}
      </Column>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: 24,
          paddingTop: 0,
          background: Colors.neutral_100,
          maxWidth: 1400,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Dashboard />
      </div>

      <SelectWorkerTypePopup />
      <PrimaryOfficeContactPopup />

      {showNotAllowedPopup && (
        <ConfirmPopup
          Icon={() => <OutlineWarning01Icon size={80} />}
          title="Select Office"
          titleStyle={{
            marginTop: 20,
          }}
          description={
            'To ensure your action is directed to the right location, please select an office by tapping "Switch Account" on the "Dashboard" page first.'
          }
          descriptionStyle={{
            marginBottom: 40,
          }}
          rightButtonText={'Close'}
          rightButtonAction={() => {
            navigate('/');
          }}
        />
      )}
    </div>
  );
};

export default DashboardPage;
